/**
 * Custom blog info — TOC from headings + active link on scroll.
 */
(function () {
  'use strict';

  var HEADER_OFFSET = 120;

  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function uniqueId(base, used) {
    var id = base || 'section';
    var n = 1;
    var candidate = id;
    while (used[candidate] || document.getElementById(candidate)) {
      n += 1;
      candidate = id + '-' + n;
    }
    used[candidate] = true;
    return candidate;
  }

  function buildToc(root) {
    var content = root.querySelector('[data-blog-info-content]');
    var tocNav = root.querySelector('[data-blog-info-toc]');
    var tocList = root.querySelector('[data-blog-info-toc-list]');
    if (!content || !tocNav || !tocList) return [];

    var headings = content.querySelectorAll('h2, h3');
    if (!headings.length) {
      tocNav.hidden = true;
      return [];
    }

    var used = {};
    var items = [];
    tocList.innerHTML = '';

    headings.forEach(function (heading) {
      var text = (heading.textContent || '').trim();
      if (!text) return;

      var id = heading.id;
      if (!id) {
        id = uniqueId(slugify(text), used);
        heading.id = id;
      } else {
        used[id] = true;
      }

      var li = document.createElement('li');
      li.className = 'custom-blog-info__toc-item';

      var link = document.createElement('a');
      link.className = 'custom-blog-info__toc-link';
      link.href = '#' + id;
      link.textContent = text;
      link.setAttribute('data-toc-target', id);

      link.addEventListener('click', function (event) {
        event.preventDefault();
        var target = document.getElementById(id);
        if (!target) return;
        var top = target.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
        window.scrollTo({ top: top, behavior: 'smooth' });
        history.replaceState(null, '', '#' + id);
      });

      li.appendChild(link);
      tocList.appendChild(li);
      items.push({ id: id, link: link, el: heading });
    });

    if (!items.length) {
      tocNav.hidden = true;
      return [];
    }

    tocNav.hidden = false;
    return items;
  }

  function syncActive(items) {
    if (!items.length) return;

    var activeId = items[0].id;
    var scrollY = window.pageYOffset + HEADER_OFFSET + 8;

    for (var i = 0; i < items.length; i += 1) {
      var top = items[i].el.getBoundingClientRect().top + window.pageYOffset;
      if (top <= scrollY) {
        activeId = items[i].id;
      } else {
        break;
      }
    }

    items.forEach(function (item) {
      if (item.id === activeId) {
        item.link.classList.add('is-active');
      } else {
        item.link.classList.remove('is-active');
      }
    });
  }

  function throttle(fn, wait) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        fn();
        setTimeout(function () {
          ticking = false;
        }, wait);
      });
    };
  }

  function copyText(text) {
    if (!text) return Promise.reject();

    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise(function (resolve, reject) {
      var textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy') ? resolve() : reject();
      } catch (error) {
        reject(error);
      } finally {
        document.body.removeChild(textarea);
      }
    });
  }

  function showCopyFeedback(link) {
    var originalTitle = link.getAttribute('title') || '';
    link.classList.add('is-copied');
    link.setAttribute('title', 'Link copied');
    link.setAttribute('aria-label', 'Link copied');

    window.setTimeout(function () {
      link.classList.remove('is-copied');
      link.setAttribute('title', originalTitle);
      link.setAttribute('aria-label', originalTitle);
    }, 2000);
  }

  function initShareLinks(root) {
    var shareList = root.querySelector('.custom-blog-info__share-list');
    if (!shareList) return;

    shareList.addEventListener(
      'click',
      function (event) {
        var control = event.target.closest('[data-blog-info-share]');
        if (!control || !shareList.contains(control)) return;

        var network = control.getAttribute('data-share-network');

        if (network === 'copy') {
          event.preventDefault();
          event.stopPropagation();

          var url = (control.getAttribute('data-share-url') || window.location.href).split('#')[0];

          copyText(url)
            .then(function () {
              showCopyFeedback(control);
            })
            .catch(function () {
              window.prompt('Copy this link:', url);
            });
        }
      },
      true
    );
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clearAccordionTransition(body) {
    if (!body) return;
    body.style.maxHeight = '';
    body.removeAttribute('data-accordion-animating');
  }

  function openAccordion(details, body, animate) {
    if (!details || !body) return;

    details.open = true;

    if (!animate || prefersReducedMotion()) {
      clearAccordionTransition(body);
      return;
    }

    body.setAttribute('data-accordion-animating', 'true');
    body.style.maxHeight = '0px';
    // Force layout so the browser starts from 0 before expanding.
    void body.offsetHeight;

    requestAnimationFrame(function () {
      body.style.maxHeight = body.scrollHeight + 'px';
    });

    var onEnd = function (event) {
      if (event.target !== body || event.propertyName !== 'max-height') return;
      body.removeEventListener('transitionend', onEnd);
      if (details.open) {
        body.style.maxHeight = 'none';
        body.removeAttribute('data-accordion-animating');
      }
    };

    body.addEventListener('transitionend', onEnd);
  }

  function closeAccordion(details, body, animate) {
    if (!details || !body) return;

    if (!animate || prefersReducedMotion()) {
      details.open = false;
      clearAccordionTransition(body);
      return;
    }

    body.setAttribute('data-accordion-animating', 'true');
    body.style.maxHeight = body.scrollHeight + 'px';
    void body.offsetHeight;

    requestAnimationFrame(function () {
      body.style.maxHeight = '0px';
    });

    var onEnd = function (event) {
      if (event.target !== body || event.propertyName !== 'max-height') return;
      body.removeEventListener('transitionend', onEnd);
      details.open = false;
      clearAccordionTransition(body);
    };

    body.addEventListener('transitionend', onEnd);
  }

  function initAccordions(root) {
    var accordions = root.querySelectorAll('.custom-blog-info__accordion');
    if (!accordions.length) return;

    accordions.forEach(function (details) {
      var body = details.querySelector('.custom-blog-info__accordion-body');
      if (details.open && body) {
        body.style.maxHeight = 'none';
      }
    });

    root.addEventListener('click', function (event) {
      var summary = event.target.closest('.custom-blog-info__accordion-summary');
      if (!summary || !root.contains(summary)) return;

      var details = summary.closest('.custom-blog-info__accordion');
      if (!details || !root.contains(details)) return;

      var body = details.querySelector('.custom-blog-info__accordion-body');
      if (!body) return;

      event.preventDefault();

      if (body.getAttribute('data-accordion-animating') === 'true') return;

      if (details.open) {
        closeAccordion(details, body, true);
      } else {
        accordions.forEach(function (other) {
          if (other === details || !other.open) return;
          closeAccordion(other, other.querySelector('.custom-blog-info__accordion-body'), true);
        });
        openAccordion(details, body, true);
      }
    });
  }

  function initRoot(root) {
    if (!root || root.getAttribute('data-blog-info-ready') === 'true') return;
    root.setAttribute('data-blog-info-ready', 'true');

    initShareLinks(root);
    initAccordions(root);

    var items = buildToc(root);
    if (!items.length) return;

    var onScroll = throttle(function () {
      syncActive(items);
    }, 80);

    window.addEventListener('scroll', onScroll, { passive: true });
    syncActive(items);
  }

  function initAll() {
    document.querySelectorAll('[data-section-type="custom-blog-info"]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target && event.target.querySelector
      ? event.target.querySelector('[data-section-type="custom-blog-info"]')
      : null;
    if (section) {
      section.removeAttribute('data-blog-info-ready');
      initRoot(section);
    } else {
      initAll();
    }
  });
})();
